import { useEffect, useRef, useState } from 'react';
import { CollectionSummary, storagePaths } from '@qkindex/contracts';
import { Item } from '../../domain/item';
import {
  createImageBlobCache,
  imageCacheIdentity,
} from '../images/imageBlobCache';
import { capturePhoto, RapidCaptureRecord } from './rapidCaptureTypes';

/** Keep one photo URL for the capture's immutable image while it remains visible. */
export function useCaptureInventoryImages(
  userId: string,
  collection: CollectionSummary | undefined,
  records: RapidCaptureRecord[],
  items: Item[],
) {
  const urls = useRef(
    new Map<
      string,
      {
        url: string;
        itemId: string;
        recordId: string;
        path?: string;
        captureOperationId?: string;
      }
    >(),
  );
  const seeded = useRef(new Set<string>());
  const [images, setImages] = useState<ReadonlyMap<string, string>>(new Map());
  const key = collection
    ? `${userId}:${collection.collectionId}:${imageCacheIdentity(collection)}`
    : '';
  useEffect(() => {
    const activeUrls = urls.current;
    const activeSeeds = seeded.current;
    return () => {
      activeUrls.forEach(({ url }) => URL.revokeObjectURL(url));
      activeUrls.clear();
      activeSeeds.clear();
    };
  }, [key]);
  useEffect(() => {
    const retained = new Set<string>();
    const projected = new Map<string, string>();
    const byId = new Map(items.map((item) => [item.id, item]));
    if (collection) {
      records.forEach((record) => {
        if (
          record.schemaVersion !== 6 ||
          record.state === 'terminal' ||
          record.userId !== userId ||
          record.collectionId !== collection.collectionId ||
          record.membershipVersion !== collection.membershipVersion ||
          record.lifecycleVersion !== collection.lifecycleVersion
        )
          return;
        const photo = capturePhoto(record);
        if (!photo?.image?.size) return;
        const item = byId.get(record.itemId);
        const path = photo.operationId
          ? storagePaths.durableImage(
              collection.collectionId,
              `${photo.operationId}.jpg`,
            )
          : undefined;
        if (
          item?.deleted ||
          (photo.committed &&
            path &&
            item &&
            item.storagePath !== path &&
            (item.revision ?? Infinity) >= (record.itemRevision ?? 0))
        )
          return;
        retained.add(photo.key);
        if (!urls.current.has(photo.key)) {
          urls.current.set(photo.key, {
            url: URL.createObjectURL(photo.image),
            itemId: record.itemId,
            recordId: record.operationId,
          });
        }
        const entry = urls.current.get(photo.key)!;
        projected.set(record.operationId, entry.url);
        if (path && item?.storagePath === path && !item.deleted) {
          entry.path = path;
          entry.captureOperationId = item.captureOperationId;
          projected.set(`item:${item.id}:${path}`, entry.url);
          if (!seeded.current.has(photo.key)) {
            seeded.current.add(photo.key);
            void createImageBlobCache(
              userId,
              collection.collectionId,
              imageCacheIdentity(collection),
            ).write(path, photo.image);
          }
        }
      });
      // Preserve the exact local URL after completion, until this media leaves inventory.
      urls.current.forEach((entry, key) => {
        const item = byId.get(entry.itemId);
        if (
          item &&
          !item.deleted &&
          entry.path &&
          item.storagePath === entry.path &&
          item.captureOperationId === entry.captureOperationId
        ) {
          retained.add(key);
          if (!projected.has(entry.recordId))
            projected.set(entry.recordId, entry.url);
          projected.set(`item:${item.id}:${entry.path}`, entry.url);
        }
      });
    }
    urls.current.forEach(({ url }, key) => {
      if (retained.has(key)) return;
      URL.revokeObjectURL(url);
      urls.current.delete(key);
      seeded.current.delete(key);
    });
    if (
      images.size !== projected.size ||
      [...projected].some(([key, url]) => images.get(key) !== url)
    )
      setImages(projected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, records, items, userId]);
  return images;
}
